import { Component } from '@theme/component';
import { adoptSectionStylesheet, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    if (this.hasAttribute('data-cart-anchor')) {
      document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * A list that follows the cart (VIS-972) is rebuilt on every cart change: the anchor may be gone
   * and the picks must leave out what was just added. The section is re-rendered first; a list it
   * can render itself (curated, fallback, empty cart) swaps in directly, otherwise the anchor
   * attributes change and the mutation observer refetches, keeping the current cards until then.
   */
  #handleCartUpdate = async () => {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const html = new DOMParser().parseFromString(await sectionRenderer.getSectionHTML(sectionId, false), 'text/html');
    const next = html.querySelector(`product-recommendations[id="${this.id}"]`);
    if (!(next instanceof HTMLElement)) return;

    this.#cachedRecommendations = {};
    this.classList.remove('hidden');

    if (next.dataset.recommendationsPerformed === 'true') {
      adoptSectionStylesheet(html);
      this.innerHTML = next.innerHTML;
      this.dataset.recommendationsPerformed = 'true';
      this.dataset.productId = next.dataset.productId ?? '';
      return;
    }

    this.dataset.productId = next.dataset.productId ?? '';
    this.dataset.recommendationsPerformed = 'false';
  };

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context. Also covers a list the section
    // rendered itself with no product to fetch for (an empty cart, VIS-972), so checked before the
    // product ID.
    if (recommendationsPerformed === 'true') {
      return;
    }

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        // Only the carousel's markup is kept below, and the product template renders no card of its
        // own, so without this the injected cards land on a page whose stylesheet has no CSS for
        // them (VIS-944: the quick add drew as a block over the image instead of the hover pill).
        adoptSectionStylesheet(html);
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);

    // A fallback collection rendered hidden next to the skeletons (VIS-972) replaces them instead.
    const fallback = this.querySelector('[data-fallback]');
    if (fallback instanceof HTMLElement) {
      this.querySelector('.product-recommendations__skeleton-item')?.parentElement?.remove();
      fallback.hidden = false;
      return;
    }

    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
